// SeletorDeSecao.js — a navegação da "Minha Conta" no celular.
//
// ================= DUAS BARRAS VIRARAM UMA (18/09/2026) =================
//
// No celular o menu lateral virava uma FILEIRA DE ABAS rolável. E várias seções
// têm as suas próprias sub-abas — Dados Pessoais tem quatro (Perfil, Redes,
// Endereços, Segurança), e elas também rolam na horizontal. O resultado eram
// duas fileiras de pílulas idênticas, empilhadas, ambas cortadas na borda da
// tela: o dono mandou o print e disse que "fica confuso pro usuário". Estava
// certo — nada na tela dizia que a de cima troca a SEÇÃO e a de baixo troca a
// PÁGINA dentro dela.
//
// A de cima virou UM BOTÃO: ele mostra onde você está e abre a lista inteira
// numa folha. A de baixo continua sendo a única fileira de abas da tela, e
// passa a ser lida como o que é.
//
// POR QUE UMA FOLHA, E NÃO UM <select>: os destaques (Loja de Créditos e Clube)
// têm cor e uma linha de apoio que explica o que são — num `<select>` nativo
// viram duas linhas de texto iguais às outras, e era justamente esse achatamento
// que a versão anterior do menu tinha vindo consertar.
//
// O QUE ELE NÃO FAZ: nada no computador. Lá a coluna lateral continua inteira,
// que é onde ela cabe sem disputar espaço com nada.
//
// ================= ELE SERVE AOS DOIS NÍVEIS (18/09/2026) =================
//
// O dono gostou do padrão e pediu o mesmo para as SUB-ABAS. Então o componente
// deixou de falar em "seção": recebe o rótulo de cima, o título da folha e os
// grupos, e serve tanto ao menu da conta quanto às abas de dentro de uma seção.
// `TINTA` virou opcional — só o menu da conta tem itens coloridos.
import React, { useEffect } from "react";

/**
 * @param {Array}  grupos      [{ titulo, itens, destaque? }]
 * @param {object} atual       o item da seção aberta (para o rótulo do botão)
 * @param {func}   estaAtivo   (item) => bool
 * @param {func}   aoEscolher  (item) => void
 * @param {object} TINTA       o mapa de cores do menu (ver Sidebar.js)
 */
export default function SeletorDeSecao({
  grupos,
  atual,
  estaAtivo,
  aoEscolher,
  TINTA = {},
  /** O rótulo pequeno em cima do nome, no botão fechado. */
  rotulo = "Minha conta",
  /** O título da folha aberta. */
  titulo = "Minha conta",
}) {
  const [aberto, setAberto] = React.useState(false);

  // Esc fecha, e o corpo para de rolar enquanto a folha está aberta — a mesma
  // dupla de regras do bottom-sheet de filtros do catálogo. Sem a trava, rolar
  // "dentro" da folha rola a página atrás dela.
  useEffect(() => {
    if (!aberto) return undefined;
    const aoTeclar = (e) => e.key === "Escape" && setAberto(false);
    const antes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.body.style.overflow = antes;
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  const tintaDe = (item) => (item.cor ? TINTA[item.cor] : null);

  return (
    <div className="md:hidden">
      <button
        type="button"
        onClick={() => setAberto(true)}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        className="foco-laranja flex w-full items-center gap-3 rounded-card border-2 border-insigne-sombra
                   bg-insigne-navy-soft px-3.5 py-3 text-left shadow-[3px_3px_0_0_rgb(var(--c-sombra))]
                   transition-colors hover:border-insigne-orange/60"
      >
        <span
          aria-hidden="true"
          className={
            "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-controle border-2 text-lg " +
            (tintaDe(atual)
              ? tintaDe(atual).parado + " " + tintaDe(atual).texto
              : "border-insigne-sombra bg-insigne-navy-deep text-insigne-orange")
          }
        >
          <i className={"bi " + (atual?.icon || "bi-person-circle")} />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">
            {rotulo}
          </span>
          <span className="block truncate font-bold text-white">{atual?.label || "Meu Perfil"}</span>
        </span>

        <i className="bi bi-chevron-expand shrink-0 text-white/45" aria-hidden="true" />
      </button>

      {aberto && (
        <div
          className="fixed inset-0 z-[9998] flex items-end bg-black/60 backdrop-blur-sm"
          onClick={() => setAberto(false)}
          role="presentation"
        >
          {/* `stopPropagation`: clicar DENTRO da folha não pode fechá-la —
              é o clique no escuro em volta que fecha. */}
          <div
            role="dialog"
            aria-label={titulo}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] w-full overflow-y-auto rounded-t-2xl border-t-2 border-insigne-sombra
                       bg-insigne-navy-deep pb-[calc(1rem+env(safe-area-inset-bottom))]"
          >
            {/* A "alça" e o título ficam grudados no alto da folha: rolando a
                lista, ainda se sabe o que está aberto e onde fechar. */}
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b-2 border-insigne-sombra bg-insigne-navy-deep px-4 py-3">
              <p className="font-bold text-white">{titulo}</p>
              <button
                type="button"
                onClick={() => setAberto(false)}
                aria-label="Fechar"
                className="foco-laranja inline-flex h-9 w-9 items-center justify-center rounded-controle border-2 border-insigne-sombra text-white/70 transition-colors hover:text-white"
              >
                <i className="bi bi-x-lg text-sm" aria-hidden="true" />
              </button>
            </div>

            <div className="space-y-4 px-4 pt-4">
              {grupos.map((grupo) => (
                <div key={grupo.titulo}>
                  {grupo.titulo && (
                    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white/35">
                      {grupo.titulo}
                    </p>
                  )}
                  <div className="flex flex-col gap-2">
                    {grupo.itens.map((item) => {
                      const ativo = estaAtivo(item);
                      const tinta = tintaDe(item);
                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => {
                            aoEscolher(item);
                            setAberto(false);
                          }}
                          aria-current={ativo ? "page" : undefined}
                          className={
                            "flex items-center gap-3 rounded-card border-2 px-3.5 py-3 text-left transition-colors " +
                            (ativo
                              ? tinta
                                ? tinta.ativo + " text-white"
                                : "border-insigne-orange bg-insigne-orange/15 text-white"
                              : tinta
                              ? tinta.parado + " text-white"
                              : "border-insigne-sombra bg-insigne-navy-soft text-white/85 hover:border-white/25")
                          }
                        >
                          <i
                            className={
                              "bi " + item.icon + " text-lg " +
                              (tinta && !ativo ? tinta.texto : ativo ? "text-white" : "text-insigne-orange")
                            }
                            aria-hidden="true"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block font-bold leading-tight">{item.label}</span>
                            {/* A linha de apoio dos destaques: é ela que
                                diferencia "Loja de Créditos" de mais um item
                                de lista. */}
                            {item.nota && (
                              <span className="mt-0.5 block text-xs text-white/55">{item.nota}</span>
                            )}
                          </span>
                          {ativo && <i className="bi bi-check-lg shrink-0" aria-hidden="true" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
